/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  layerSelectors,
  mapActions,
  mapEnums,
  mapSelectors,
} from '@opengeoweb/store';
import type { AnimationLengthSetting } from '@opengeoweb/time-slider';
import {
  AnimationLengthMode,
  AnimationLengthPanel,
  isAnchoredMode,
  isValidAnimationLength,
  isWithinAnimationStepLimit,
  exactAnimationLengthFrom,
  settingRange,
} from '@opengeoweb/time-slider';

export interface AnimationLengthPanelConnectProps {
  mapId: string;
}

export const AnimationLengthPanelConnect: React.FC<
  AnimationLengthPanelConnectProps
> = ({ mapId }: AnimationLengthPanelConnectProps) => {
  const dispatch = useDispatch();

  const storedAnimationLength = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationLength(store, mapId),
  );
  const isAnimating = useSelector((store: CoreAppStore) =>
    mapSelectors.isAnimating(store, mapId),
  );

  const [pendingLength, setPendingLength] =
    React.useState<AnimationLengthSetting>();
  const isCommittableRef = React.useRef(false);

  const currentMode = storedAnimationLength?.mode ?? AnimationLengthMode.Exact;
  const draftLength =
    pendingLength?.mode === currentMode ? pendingLength : undefined;

  const debouncedLength = useDebounce(pendingLength, 500);

  React.useEffect(() => {
    setPendingLength(undefined);
  }, [storedAnimationLength, mapId]);

  const { animationStartTime, animationEndTime } = useSelector(
    (store: CoreAppStore) => mapSelectors.getAnimationRange(store, mapId),
  );

  const autoTimeStepLayerId = useSelector((store: CoreAppStore) =>
    mapSelectors.getAutoTimeStepLayerId(store, mapId),
  );

  const autoTimeStepLayerName = useSelector((store: CoreAppStore) =>
    autoTimeStepLayerId
      ? layerSelectors.getLayerTitleFromService(store, autoTimeStepLayerId)
      : undefined,
  );

  const timeStepFromLayer = useSelector((store: CoreAppStore) =>
    layerSelectors.getTimeStepForLayerId(store, autoTimeStepLayerId),
  );

  const timeStep = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeStep(store, mapId),
  );

  const anchorInputs = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationAnchorInputs(store, mapId),
  );

  const commitAnimationLength = React.useCallback(
    (length: AnimationLengthSetting): void => {
      const isUsableRange =
        isValidAnimationLength(length, anchorInputs) &&
        isWithinAnimationStepLimit(length, timeStep, anchorInputs);
      const carriesOwnRange =
        isAnchoredMode(length.mode) ||
        length.mode === AnimationLengthMode.Exact;
      const resolvedRange =
        carriesOwnRange && isUsableRange
          ? settingRange(length, anchorInputs)
          : undefined;

      dispatch(
        mapActions.setAnimationLength({
          mapId,
          animationLength: length,
          ...resolvedRange,
          origin: mapEnums.MapActionOrigin.map,
        }),
      );
    },
    [anchorInputs, dispatch, mapId, timeStep],
  );

  React.useEffect(() => {
    if (debouncedLength && isCommittableRef.current) {
      commitAnimationLength(debouncedLength);
      setPendingLength(undefined);
    }
  }, [debouncedLength, commitAnimationLength]);

  const changeAnimationLength = (
    animationLength: AnimationLengthSetting,
    isStep?: boolean,
  ): void => {
    const shouldDefer =
      isAnimating &&
      animationLength.mode === currentMode &&
      (isAnchoredMode(currentMode) ||
        currentMode === AnimationLengthMode.Exact);

    isCommittableRef.current = shouldDefer;
    setPendingLength(animationLength);

    if (!shouldDefer) {
      commitAnimationLength(animationLength);
      return;
    }

    if (isStep) {
      const isUsableRange =
        isValidAnimationLength(animationLength, anchorInputs) &&
        isWithinAnimationStepLimit(animationLength, timeStep, anchorInputs);
      if (isUsableRange) {
        dispatch(
          mapActions.refreshAnimationRange({
            mapId,
            ...settingRange(animationLength, anchorInputs),
          }),
        );
      }
    }
  };

  const cancelPendingCommit = (): void => {
    isCommittableRef.current = false;
  };

  return (
    <AnimationLengthPanel
      animationEndTime={
        draftLength?.mode === AnimationLengthMode.Exact
          ? draftLength.exact.animationEndTime
          : animationEndTime
      }
      animationStartTime={
        draftLength?.mode === AnimationLengthMode.Exact
          ? draftLength.exact.animationStartTime
          : animationStartTime
      }
      autoTimeStepLayerName={autoTimeStepLayerName}
      isAutoDisabled={!timeStepFromLayer}
      onChange={changeAnimationLength}
      onInputChange={cancelPendingCommit}
      referenceTimeFromLayer={anchorInputs.referenceTime}
      setting={
        draftLength ??
        storedAnimationLength ??
        exactAnimationLengthFrom({ animationStartTime, animationEndTime })
      }
      timeStep={timeStep}
    />
  );
};
